import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCatalogoDdjj, type PreguntaCatalogoDdjj } from '@lib/services/empresas.service';

/**
 * Selector de las preguntas de la declaración jurada que hace una empresa.
 *
 * Se muestra el catálogo COMPLETO con todo tildado por default y el admin
 * destilda lo que no quiere preguntar. Lo que viaja al server es la lista de
 * APAGADAS (`value`), no la de prendidas: así una pregunta nueva del catálogo
 * queda prendida sola en todas las empresas.
 *
 * Apagar una pregunta de la que otra depende arrastra a la dependiente (el
 * server hace lo mismo en cascada); acá se refleja deshabilitando la hija y
 * aclarando de quién cuelga, para que no parezca un tilde que no responde.
 */
export function DdjjPreguntasSelector({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (excluidas: string[]) => void;
  disabled?: boolean;
}) {
  const catalogoQ = useQuery({
    queryKey: ['empresas', 'catalogo-ddjj'],
    queryFn: getCatalogoDdjj,
    staleTime: 5 * 60 * 1000,
  });

  const excluidas = useMemo(() => new Set(value), [value]);
  const preguntas = useMemo(() => catalogoQ.data ?? [], [catalogoQ.data]);

  // Agrupadas por sección, respetando el orden del catálogo.
  const secciones = useMemo(() => {
    const map = new Map<number, { titulo: string; preguntas: PreguntaCatalogoDdjj[] }>();
    for (const p of preguntas) {
      const sec = map.get(p.seccion) ?? { titulo: p.seccionTitulo, preguntas: [] };
      sec.preguntas.push(p);
      map.set(p.seccion, sec);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [preguntas]);

  /** Apagada de verdad: por sí misma o porque cayó su padre. */
  const apagadaEfectiva = (p: PreguntaCatalogoDdjj): boolean =>
    excluidas.has(p.codigo) || (!!p.dependeDe && excluidas.has(p.dependeDe));

  const toggle = (codigo: string) => {
    const next = new Set(excluidas);
    if (next.has(codigo)) next.delete(codigo);
    else next.add(codigo);
    onChange([...next]);
  };

  const toggleSeccion = (sec: PreguntaCatalogoDdjj[], prender: boolean) => {
    const next = new Set(excluidas);
    for (const p of sec) {
      if (prender) next.delete(p.codigo);
      else next.add(p.codigo);
    }
    onChange([...next]);
  };

  if (catalogoQ.isLoading) {
    return <p className="text-element-mid">Cargando preguntas…</p>;
  }
  if (catalogoQ.isError) {
    return (
      <p style={{ color: 'var(--color-error-dark)' }}>
        No se pudo cargar el catálogo de preguntas.
      </p>
    );
  }

  const totalApagadas = preguntas.filter(apagadaEfectiva).length;

  return (
    <div>
      <p className="text-element-mid" style={{ marginBottom: 12 }}>
        Se hacen todas las preguntas salvo las que destildes.{' '}
        <strong>
          {preguntas.length - totalApagadas} de {preguntas.length}
        </strong>{' '}
        activas.
      </p>

      {secciones.map(([nro, sec]) => {
        const todasApagadas = sec.preguntas.every((p) => excluidas.has(p.codigo));
        return (
          <fieldset
            key={nro}
            style={{
              border: '1px solid var(--color-outline)',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 10,
            }}
          >
            <legend
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 13,
                fontWeight: 600,
                padding: '0 6px',
              }}
            >
              {sec.titulo}
              <button
                type="button"
                className="btn btn--ghost"
                style={{ fontSize: 11, padding: '2px 8px' }}
                disabled={disabled}
                onClick={() => toggleSeccion(sec.preguntas, todasApagadas)}
              >
                {todasApagadas ? 'Activar todas' : 'Desactivar todas'}
              </button>
            </legend>

            {sec.preguntas.map((p) => {
              const porPadre = !!p.dependeDe && excluidas.has(p.dependeDe);
              return (
                <label
                  key={p.codigo}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '4px 0',
                    opacity: porPadre ? 0.55 : 1,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!apagadaEfectiva(p)}
                    disabled={disabled || porPadre}
                    onChange={() => toggle(p.codigo)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ fontSize: 13 }}>
                    {p.pregunta}
                    {p.soloTitular && (
                      <span className="text-element-mid" style={{ fontSize: 11 }}>
                        {' '}
                        · solo al titular
                      </span>
                    )}
                    {p.opcional && (
                      <span className="text-element-mid" style={{ fontSize: 11 }}>
                        {' '}
                        · opcional
                      </span>
                    )}
                    {porPadre && (
                      <span className="text-element-mid" style={{ fontSize: 11 }}>
                        {' '}
                        · se apaga con la pregunta de la que depende
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </fieldset>
        );
      })}
    </div>
  );
}
